// pages/SettingsPage.jsx
// User Settings Page - Comprehensive Settings Management
// ============================================
// Includes: Appearance, Notifications, Privacy, Account
// ============================================

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, gql } from '@apollo/client';
import {
  ArrowLeft,
  Moon,
  Sun,
  Bell,
  BellOff,
  Mail,
  MessageSquare,
  Shield,
  Eye,
  EyeOff,
  Trash2,
  Download,
  HelpCircle,
  ChevronRight,
  Camera,
  User,
  AlertTriangle,
  Check,
  X,
  Loader2,
  Info,
  Smartphone,
  Volume2,
  VolumeX,
  Type,
  Contrast,
  Zap,
  FileText,
  ExternalLink,
  Award,
  Briefcase,
  TrendingDown,
  Trophy,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNotification } from '../context/NotificationContext';
import { uploadProfilePicture, getProfilePictureUrl, getInitialsAvatar } from '../services/cloudinaryService';
import BottomNav from '../components/common/BottomNav';
import { BookOpen, /* ...other imports */ } from 'lucide-react';

// GraphQL mutation for leaderboard toggle
const TOGGLE_LEADERBOARD = gql`
  mutation ToggleLeaderboardVisibility($show: Boolean!) {
    toggleLeaderboardVisibility(show: $show) {
      success
      showOnLeaderboard
      message
    }
  }
`;

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, firebaseUser, deleteAccount, getActiveUser } = useAuth();
  const { isDarkMode, preferences, toggleDarkMode, updatePreference } = useTheme();
  const { addNotification } = useNotification();
  
  const activeUser = getActiveUser?.() || user;
  const fileInputRef = useRef(null);
  
  // Local state
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(
    activeUser?.photoURL || activeUser?.profile?.profileImage || null
  );
  
  // Trust Badge Warning Modal State
  const [showTrustBadgeWarning, setShowTrustBadgeWarning] = useState(false);
  
  // Leaderboard toggle mutation
  const [toggleLeaderboard] = useMutation(TOGGLE_LEADERBOARD, {
    onCompleted: (data) => {
      if (data.toggleLeaderboardVisibility.success) {
        updatePreference('showOnLeaderboard', data.toggleLeaderboardVisibility.showOnLeaderboard);
        addNotification({
          type: 'success',
          title: data.toggleLeaderboardVisibility.showOnLeaderboard ? 'Visible on Leaderboard' : 'Hidden from Leaderboard',
          message: data.toggleLeaderboardVisibility.message,
          showToast: true,
        });
      }
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Update Failed',
        message: error.message || 'Failed to update leaderboard preference',
        showToast: true,
      });
    }
  });

  // Handle leaderboard toggle
  const handleLeaderboardToggle = (show) => {
    toggleLeaderboard({ variables: { show } });
  };

  // Handle profile photo upload
  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(true);

    try {
      const result = await uploadProfilePicture(file, activeUser?.id);
      
      if (result.success) {
        setProfilePhoto(result.medium);
        
        // Update user profile in backend
        const token = localStorage.getItem('token');
        if (token) {
          await fetch(`${process.env.REACT_APP_API_URL || ''}/api/auth/update-profile`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              profileImage: result.url,
            }),
          });
        }

        // Update localStorage
        const savedUser = JSON.parse(localStorage.getItem('uniledge_member_auth') || '{}');
        savedUser.photoURL = result.medium;
        localStorage.setItem('uniledge_member_auth', JSON.stringify(savedUser));

        addNotification({
          type: 'success',
          title: 'Profile Photo Updated',
          message: 'Your profile photo has been updated successfully.',
          showToast: true,
        });
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Photo upload error:', error);
      addNotification({
        type: 'error',
        title: 'Upload Failed',
        message: error.message || 'Failed to upload profile photo. Please try again.',
        showToast: true,
      });
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Handle Trust Badge Toggle with Warning
  const handleTrustBadgeToggle = (newValue) => {
    if (!newValue) {
      // User is trying to HIDE badges - show warning first
      setShowTrustBadgeWarning(true);
    } else {
      // User is turning badges ON - no warning needed
      updatePreference('showTrustBadges', true);
      addNotification({
        type: 'success',
        title: 'Trust Badges Visible',
        message: 'Your trust badges are now visible. You have full access to all opportunities!',
        showToast: true,
      });
    }
  };

  // Confirm hiding trust badges after warning
  const confirmHideTrustBadges = () => {
    updatePreference('showTrustBadges', false);
    setShowTrustBadgeWarning(false);
    addNotification({
      type: 'info',
      title: 'Trust Badges Hidden',
      message: 'Your trust badges are now hidden. Some opportunities may be limited.',
      showToast: true,
    });
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      addNotification({
        type: 'error',
        title: 'Confirmation Required',
        message: 'Please type DELETE to confirm account deletion.',
        showToast: true,
      });
      return;
    }

    setDeleting(true);

    try {
      const result = await deleteAccount();
      
      if (result.success) {
        addNotification({
          type: 'success',
          title: 'Account Deleted',
          message: 'Your account has been deleted. We\'re sorry to see you go.',
          showToast: true,
        });
        navigate('/welcome');
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Account deletion error:', error);
      addNotification({
        type: 'error',
        title: 'Deletion Failed',
        message: error.message || 'Failed to delete account. Please try again or contact support.',
        showToast: true,
      });
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
      setDeleteConfirmText('');
    }
  };

  // Handle data export
  const handleExportData = async () => {
    try {
      // Compile user data
      const exportData = {
        profile: {
          name: activeUser?.name,
          email: activeUser?.email,
          phone: activeUser?.phone,
          joinedDate: activeUser?.joinedDate || activeUser?.profile?.createdAt,
        },
        preferences: preferences,
        exportedAt: new Date().toISOString(),
      };

      // Create and download file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `uniledge-data-export-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      addNotification({
        type: 'success',
        title: 'Data Exported',
        message: 'Your data has been downloaded successfully.',
        showToast: true,
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Export Failed',
        message: 'Failed to export data. Please try again.',
        showToast: true,
      });
    }
  };

  // Toggle component
  const Toggle = ({ enabled, onChange, disabled = false }) => (
    <button
      onClick={() => !disabled && onChange(!enabled)}
      disabled={disabled}
      className={`relative w-11 h-6 rounded-full transition-colors ${
        enabled ? 'bg-blue-600' : 'bg-gray-300'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <div
        className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
          enabled ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );

  // Section component
  const Section = ({ title, children }) => (
    <div className="bg-white rounded-lg shadow-sm mb-4">
      <div className="px-4 py-3 border-b">
        <h3 className="font-semibold text-gray-800">{title}</h3>
      </div>
      <div className="divide-y">{children}</div>
    </div>
  );

  // Setting row component
  const SettingRow = ({ icon: Icon, label, description, children, onClick }) => (
    <div
      className={`px-4 py-3 flex items-center justify-between ${
        onClick ? 'cursor-pointer hover:bg-gray-50' : ''
      }`}
      onClick={onClick}
    >
      <div className="flex items-center flex-1">
        {Icon && <Icon size={20} className="text-gray-500 mr-3" />}
        <div className="flex-1">
          <p className="text-gray-800">{label}</p>
          {description && <p className="text-sm text-gray-500">{description}</p>}
        </div>
      </div>
      {children}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-xl font-bold">Settings</h1>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-lg">
        {/* Profile Photo Section */}
        <Section title="Profile">
          <div className="px-4 py-4">
            <div className="flex items-center">
              <div className="relative">
                <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-200">
                  {uploadingPhoto ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <Loader2 className="animate-spin text-blue-600" size={24} />
                    </div>
                  ) : (
                    <img
                      src={profilePhoto || getInitialsAvatar(activeUser?.name || 'User')}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="absolute bottom-0 right-0 bg-blue-600 text-white p-1.5 rounded-full hover:bg-blue-700 disabled:opacity-50"
                >
                  <Camera size={14} />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handlePhotoSelect}
                  className="hidden"
                />
              </div>
              <div className="ml-4">
                <p className="font-medium text-gray-800">{activeUser?.name || 'User'}</p>
                <p className="text-sm text-gray-500">{activeUser?.email}</p>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="text-sm text-blue-600 hover:text-blue-700 mt-1"
                >
                  Change Photo
                </button>
              </div>
            </div>
          </div>
        </Section>

        {/* Appearance */}
        <Section title="Appearance">
          <SettingRow
            icon={isDarkMode ? Moon : Sun}
            label="Dark Mode"
            description="Switch between light and dark themes"
          >
            <Toggle enabled={isDarkMode} onChange={toggleDarkMode} />
          </SettingRow>
          
          <SettingRow
            icon={Type}
            label="Large Text"
            description="Increase text size for readability"
          >
            <Toggle
              enabled={preferences.largeText}
              onChange={(val) => updatePreference('largeText', val)}
            />
          </SettingRow>
          
          <SettingRow
            icon={Zap}
            label="Reduce Motion"
            description="Minimize animations"
          >
            <Toggle
              enabled={preferences.reducedMotion}
              onChange={(val) => updatePreference('reducedMotion', val)}
            />
          </SettingRow>
        </Section>

        {/* Learning & Tours */}
        <Section title="Learning & Tours">
          <SettingRow
            icon={BookOpen}
            label="Retake Platform Tours"
            description="Review tours to refresh your knowledge"
            onClick={() => {
              // Open AVA and switch to tours tab
              window.dispatchEvent(new CustomEvent('ava-open', {
                detail: { openTab: 'tours' }
              }));
            }}
          >
            <ChevronRight size={20} className="text-gray-400" />
          </SettingRow>
  
          <SettingRow
            icon={Award}
            label="Reset Tour Progress"
            description="Clear completion status to earn tokens again"
            onClick={() => {
              if (window.confirm('Reset all tour progress? You can earn tour tokens again.')) {
                localStorage.removeItem('ava_tours_completed');
                localStorage.removeItem('ava_vendor_tours_completed');
                addNotification({
                  type: 'success',
                  title: 'Tours Reset',
                  message: 'You can now retake all tours and earn tokens!',
                  showToast: true
                });
              }
            }}
          >
            <ChevronRight size={20} className="text-gray-400" />
          </SettingRow>
        </Section>

        {/* Notifications */}
        <Section title="Notifications">
          <SettingRow
            icon={Bell}
            label="Push Notifications"
            description="Receive push notifications on your device"
          >
            <Toggle
              enabled={preferences.pushNotifications}
              onChange={(val) => updatePreference('pushNotifications', val)}
            />
          </SettingRow>
          
          <SettingRow
            icon={Mail}
            label="Email Notifications"
            description="Receive important updates via email"
          >
            <Toggle
              enabled={preferences.emailNotifications}
              onChange={(val) => updatePreference('emailNotifications', val)}
            />
          </SettingRow>
          
          <SettingRow
            icon={Smartphone}
            label="SMS Notifications"
            description="Receive text messages for urgent updates"
          >
            <Toggle
              enabled={preferences.smsNotifications}
              onChange={(val) => updatePreference('smsNotifications', val)}
            />
          </SettingRow>
        </Section>

        {/* Email Preferences */}
        <Section title="Email Preferences">
          <SettingRow
            icon={Mail}
            label="Order Updates"
            description="Updates about your orders and deliveries"
          >
            <Toggle
              enabled={preferences.emailOrderUpdates}
              onChange={(val) => updatePreference('emailOrderUpdates', val)}
            />
          </SettingRow>
          
          <SettingRow
            icon={Mail}
            label="Deals & Offers"
            description="New deals and special offers near you"
          >
            <Toggle
              enabled={preferences.emailDealsAlerts}
              onChange={(val) => updatePreference('emailDealsAlerts', val)}
            />
          </SettingRow>
          
          <SettingRow
            icon={Mail}
            label="Weekly Digest"
            description="Weekly summary of savings and activity"
          >
            <Toggle
              enabled={preferences.emailWeeklyDigest}
              onChange={(val) => updatePreference('emailWeeklyDigest', val)}
            />
          </SettingRow>
          
          <SettingRow
            icon={Mail}
            label="Marketing"
            description="News, tips, and promotional content"
          >
            <Toggle
              enabled={preferences.emailMarketing}
              onChange={(val) => updatePreference('emailMarketing', val)}
            />
          </SettingRow>
        </Section>

        {/* Privacy */}
        <Section title="Privacy">
          <SettingRow
            icon={Award}
            label="Show Trust Badges"
            description="Display your trust score and badges publicly"
          >
            <Toggle
              enabled={preferences.showTrustBadges}
              onChange={handleTrustBadgeToggle}
            />
          </SettingRow>
          
          <SettingRow
            icon={Eye}
            label="Show Activity Status"
            description="Let others see when you're active"
          >
            <Toggle
              enabled={preferences.showActivityStatus}
              onChange={(val) => updatePreference('showActivityStatus', val)}
            />
          </SettingRow>
          
          <SettingRow
            icon={Trophy}
            label="Show on Leaderboard"
            description="Appear in achievement rankings"
          >
            <Toggle
              enabled={preferences.showOnLeaderboard ?? true}
              onChange={(val) => handleLeaderboardToggle(val)}
            />
          </SettingRow>
          
          <SettingRow
            icon={Download}
            label="Export Your Data"
            description="Download a copy of your data"
            onClick={handleExportData}
          >
            <ChevronRight size={20} className="text-gray-400" />
          </SettingRow>
        </Section>

        {/* Support & Info */}
        <Section title="Support & Info">
          <SettingRow
            icon={HelpCircle}
            label="Help Center"
            description="FAQs and support articles"
            onClick={() => navigate('/support')}
          >
            <ChevronRight size={20} className="text-gray-400" />
          </SettingRow>
          
          <SettingRow
            icon={MessageSquare}
            label="Contact Support"
            description="Get help from our team"
            onClick={() => navigate('/support')}
          >
            <ChevronRight size={20} className="text-gray-400" />
          </SettingRow>
          
          <SettingRow
            icon={FileText}
            label="Terms of Service"
            description="Read our terms and conditions"
            onClick={() => window.open('/terms', '_blank')}
          >
            <ExternalLink size={16} className="text-gray-400" />
          </SettingRow>
          
          <SettingRow
            icon={Shield}
            label="Privacy Policy"
            description="How we handle your data"
            onClick={() => window.open('/privacy', '_blank')}
          >
            <ExternalLink size={16} className="text-gray-400" />
          </SettingRow>
        </Section>

        {/* About */}
        <Section title="About">
          <SettingRow
            icon={Info}
            label="Version"
            description="UniLedge v1.0.0"
          />
          
          <SettingRow
            icon={Info}
            label="Build"
            description={`Build ${Date.now().toString(36).toUpperCase()}`}
          />
        </Section>

        {/* Danger Zone */}
        <Section title="Account">
          <SettingRow
            icon={Trash2}
            label="Delete Account"
            description="Permanently delete your account and data"
            onClick={() => setShowDeleteConfirm(true)}
          >
            <ChevronRight size={20} className="text-red-400" />
          </SettingRow>
        </Section>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500 mt-6">
          <p>© 2025 UniLedge. All rights reserved.</p>
          <p className="mt-1">Made with ❤️ in Sacramento</p>
        </div>
      </div>

      {/* Trust Badge Warning Modal */}
      {showTrustBadgeWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="bg-amber-100 p-2 rounded-full mr-3">
                <AlertTriangle className="text-amber-600" size={24} />
              </div>
              <h3 className="text-lg font-bold text-gray-800">Hide Trust Badges?</h3>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-600 mb-4">
                Hiding your trust badges may <strong>limit your earning opportunities</strong> on UniLedge.
              </p>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-amber-800 mb-2 flex items-center">
                  <TrendingDown size={16} className="mr-2" />
                  What you may lose access to:
                </h4>
                <ul className="text-sm text-amber-700 space-y-2">
                  <li className="flex items-start">
                    <Briefcase size={14} className="mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Premium Jobs:</strong> Vendors prefer verified members for delivery and coordination roles</span>
                  </li>
                  <li className="flex items-start">
                    <Award size={14} className="mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Coordinator Status:</strong> Must have visible badges to become a neighborhood coordinator</span>
                  </li>
                  <li className="flex items-start">
                    <TrendingDown size={14} className="mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>~30% fewer opportunities:</strong> Some jobs require visible trust verification</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2 flex items-center">
                  <Info size={16} className="mr-2" />
                  Your privacy is still protected
                </h4>
                <p className="text-sm text-blue-700">
                  Even with badges visible, your personal information (address, phone, email) remains private. 
                  Only your trust score and verification badges are shown.
                </p>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowTrustBadgeWarning(false)}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center"
              >
                <Award size={16} className="mr-2" />
                Keep Badges Visible
              </button>
              <button
                onClick={confirmHideTrustBadges}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center justify-center"
              >
                <EyeOff size={16} className="mr-2" />
                Hide Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="bg-red-100 p-2 rounded-full mr-3">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <h3 className="text-lg font-bold text-gray-800">Delete Account?</h3>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-600 mb-3">
                This action <strong>cannot be undone</strong>. This will permanently delete your account and remove all of your data including:
              </p>
              <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                <li>Your profile and settings</li>
                <li>Order history and transactions</li>
                <li>Token balance and rewards</li>
                <li>Group memberships</li>
                <li>All personal data</li>
              </ul>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <strong>DELETE</strong> to confirm:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                placeholder="DELETE"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmText('');
                }}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleting || deleteConfirmText !== 'DELETE'}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {deleting ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={16} />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} className="mr-2" />
                    Delete Account
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default SettingsPage;